import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
  TextField,
  Typography,
  SelectChangeEvent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import sampleFeatureRaw from '../../assets/sample-model-features/sample-feature-raw.png';
import sampleFeatureFinal from '../../assets/sample-model-features/sample-feature-final.png';

interface Feature {
  id: string;
  name: string;
}

interface TemplateChangeModalProps {
  open: boolean;
  onClose: () => void;
  features: Feature[];
  onSubmit: (selectedFeatures: string[], description: string, originalImage: File | null, finalImage: File | null) => void;
  // Novo: pré-seleção e bloqueio do combobox de funcionalidades
  initialSelectedFeatureIds?: string[];
  lockFeatureSelection?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const TemplateChangeModal: React.FC<TemplateChangeModalProps> = ({
  open,
  onClose,
  features,
  onSubmit,
  initialSelectedFeatureIds,
  lockFeatureSelection,
}) => {
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [finalImage, setFinalImage] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [finalImageUrl, setFinalImageUrl] = useState<string | null>(null);
  const originalFileInputRef = useRef<HTMLInputElement | null>(null);
  const finalFileInputRef = useRef<HTMLInputElement | null>(null);

  // Flag para ajustar título e textos quando aberto via "Cadastrar Template"
  const isRegisterFlow = !!lockFeatureSelection;
  // Limpa estados e aplica pré-seleção ao abrir a modal
  useEffect(() => {
    if (open) {
      // Reset de campos para evitar carregamentos de cenários anteriores
      setDescription('');
      setOriginalImage(null);
      setFinalImage(null);
      setOriginalImageUrl(null);
      setFinalImageUrl(null);
      if (originalFileInputRef.current) {
        originalFileInputRef.current.value = '';
      }
      if (finalFileInputRef.current) {
        finalFileInputRef.current.value = '';
      }
      // Aplica pré-seleção de funcionalidades se fornecida
      setSelectedFeatures(initialSelectedFeatureIds ?? []);
    }
  }, [open, initialSelectedFeatureIds]);

  useEffect(() => {
    if (originalImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImageUrl(e.target?.result as string);
      };
      reader.readAsDataURL(originalImage);
    } else {
      setOriginalImageUrl(null);
    }
  }, [originalImage]);

  useEffect(() => {
    if (finalImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFinalImageUrl(e.target?.result as string);
      };
      reader.readAsDataURL(finalImage);
    } else {
      setFinalImageUrl(null);
    }
  }, [finalImage]);
  const handleFeatureChange = (event: SelectChangeEvent<typeof selectedFeatures>) => {
    const {
      target: { value },
    } = event;
    setSelectedFeatures(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleSubmit = () => {
    if (selectedFeatures.length === 0) {
      return;
    }
    onSubmit(selectedFeatures, description, originalImage, finalImage);
    // A modal será fechada pelo componente pai apenas em caso de sucesso.
  };

  const handleClose = () => {
    setSelectedFeatures([]);
    setDescription('');
    setOriginalImage(null);
    setFinalImage(null);
    onClose();
  };

  const getFeatureName = (featureId: string) => {
    const feature = features.find(f => f.id === featureId);
    return feature ? feature.name : featureId;
  };

  const dialogPaperSx = { overflowX: 'auto' };
  const dialogContentSx = { overflowX: 'auto' };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle>{isRegisterFlow ? 'Solicitação de cadastro de Template' : 'Solicitar Alterações de Template'}</DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Para cadastrar seu template, anexe duas imagens de referência:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          1) Arquivo base do template para redes sociais, SEM valores preenchidos (por exemplo, sem Nome do Draw, Número de Convites etc.).
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          2) Imagem final com os valores preenchidos (ex.: Nome do Draw, Número de Convites etc.), exatamente como ela deve aparecer quando um novo draw for identificado. Usaremos ela como referência para o resultado final que o seu negócio precisa.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Com essas referências, faremos o adastro do seu template e o disponibilizaremos nas próximas notificações.
        </Typography>
        {/* Exemplo de imagens para cadastro/automatização de template */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Exemplo de imagens para cadastro/automatização de template
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', overflowX: 'auto' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="subtitle2" gutterBottom>
              Imagem Original (sem parâmetros)
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#fff' }}>
              <Box
                component="img"
                src={sampleFeatureRaw}
                alt="Exemplo de imagem original sem parâmetros"
                sx={{ width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="subtitle2" gutterBottom>
              Imagem Final (com parâmetros aplicados)
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#fff' }}>
              <Box
                component="img"
                src={sampleFeatureFinal}
                alt="Exemplo de imagem final com parâmetros aplicados"
                sx={{ width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Como base nas referências acima, anexe as suas imagens de templates de forma equivalente: a imagem "original" sem valores de parâmetros e a imagem "final" com os valores do Draw aplicados.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', overflowX: 'auto' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Button variant="outlined" component="label">
              Anexar Imagem Original
              <input ref={originalFileInputRef} type="file" accept="image/*" hidden onChange={(e) => setOriginalImage(e.target.files?.[0] ?? null)} />
            </Button>
            {/* Substitui exibição simples do nome por preview com remoção */}
            {originalImage && originalImageUrl && (
              <>
                <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#fff', mt: 1 }}>
                  <Box
                    component="img"
                    src={originalImageUrl}
                    alt={`Pré-visualização da imagem original: ${originalImage?.name ?? ''}`}
                    sx={{ width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', display: 'block' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {originalImage?.name}
                  </Typography>
                  <Tooltip title={`Remover arquivo '${originalImage?.name ?? ''}'.`}>
                    <IconButton
                      aria-label="Remover imagem original"
                      size="small"
                      sx={{ color: 'error.main', '&:hover': { color: 'error.dark' } }}
                      onClick={() => {
                        setOriginalImage(null);
                        setOriginalImageUrl(null);
                        if (originalFileInputRef.current) {
                          originalFileInputRef.current.value = '';
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Button variant="outlined" component="label">
              Anexar Imagem Final
              <input ref={finalFileInputRef} type="file" accept="image/*" hidden onChange={(e) => setFinalImage(e.target.files?.[0] ?? null)} />
            </Button>
            {/* Substitui exibição simples do nome por preview com remoção */}
            {finalImage && finalImageUrl && (
              <>
                <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#fff', mt: 1 }}>
                  <Box
                    component="img"
                    src={finalImageUrl}
                    alt={`Pré-visualização da imagem final: ${finalImage?.name ?? ''}`}
                    sx={{ width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', display: 'block' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {finalImage?.name}
                  </Typography>
                  <Tooltip title={`Remover arquivo '${finalImage?.name ?? ''}'.`}>
                    <IconButton
                      aria-label="Remover imagem final"
                      size="small"
                      sx={{ color: 'error.main', '&:hover': { color: 'error.dark' } }}
                      onClick={() => {
                        setFinalImage(null);
                        setFinalImageUrl(null);
                        if (finalFileInputRef.current) {
                          finalFileInputRef.current.value = '';
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        </Box>
        {!isRegisterFlow && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione as funcionalidades que precisam de alterações nos templates e descreva brevemente o que precisa ser modificado.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Observação: nossa equipe leva cerca de até 48 horas úteis para cada alteração de template.
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="features-select-label">Funcionalidades</InputLabel>
          <Select
            labelId="features-select-label"
            id="features-select"
            multiple
            value={selectedFeatures}
            onChange={handleFeatureChange}
            input={<OutlinedInput id="select-multiple-chip" label="Funcionalidades" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={getFeatureName(value)} />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
            disabled={!!lockFeatureSelection}
          >
            {features.map((feature) => (
              <MenuItem key={feature.id} value={feature.id}>
                {feature.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Descrição das alterações"
          placeholder="Descreva brevemente as alterações necessárias nos templates..."
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
          variant="outlined"
          inputProps={{ maxLength: 1000 }}
          helperText={`${description.length}/1000`}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Importante: informe também a Fonte utilizada nos valores parametrizados da imagem final (por exemplo: Roboto, Open Sans).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={selectedFeatures.length === 0}
        >
          Solicitar Alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
};
